import { useEffect } from 'react';

/**
 * iOS WebKit click-suppression workaround.
 *
 * Symptom: on iOS (Safari + Telegram WebView) after route navigation TO `/dashboard`,
 * iOS WebKit suppresses synthetic `click` event after `touchstart`+`touchend` on
 * any clickable element for ~3 s while widget mount activity (React Query fetches,
 * Swiper.js init, IntersectionObservers) keeps the DOM in flux. Touch and pointer
 * events fire normally; only `click` is missing, so anchors, NavLinks, and onClick
 * handlers don't run. User has to tap 2-3 times before navigation registers.
 *
 * Confirmed via instrumented event log: failed taps fire pointerdown → pointermove
 * → pointerup → touchend with NO mousedown / mouseup / click follow-up; the same
 * gesture 1-3 s later produces the full sequence ending in click.
 *
 * Workaround: capture touchstart + touchend on `document` (passive). On a tap-shaped
 * gesture (low movement, ≤300 ms), wait 100 ms to see if iOS dispatches `click`
 * organically. If not, dispatch `target.click()` ourselves so React's synthetic
 * event delegation runs the handler. 100 ms is 2× the maximum touchend→click
 * latency observed in production logs (50 ms; mean ~37 ms) — generous enough
 * not to race iOS, but short enough that the synthesised path feels close to
 * native tap responsiveness. A trailing iOS click that arrives after our timer
 * fires is harmless: NavLink's `navigate()` and `setOpen(true)`-style handlers
 * are idempotent.
 *
 * Scope: iOS only (UA check). Other platforms don't reproduce the bug, and we
 * avoid risk of double-firing or shadowing native handlers there.
 */

const isIOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent);

const TAP_MAX_MOVEMENT_PX = 10;
const TAP_MAX_DURATION_MS = 300;
const CLICK_WAIT_MS = 100;

export const useIOSClickFallback = () => {
  useEffect(() => {
    if (!isIOS) return;

    let touchTarget: EventTarget | null = null;
    let touchTime = 0;
    let touchX = 0;
    let touchY = 0;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        touchTarget = null;
        return;
      }
      touchTarget = e.target;
      touchTime = performance.now();
      touchX = e.touches[0].clientX;
      touchY = e.touches[0].clientY;
    };

    const onTouchEnd = (e: TouchEvent) => {
      const target = touchTarget;
      touchTarget = null;
      if (!target) return;

      const elapsed = performance.now() - touchTime;
      if (elapsed > TAP_MAX_DURATION_MS) return;

      const last = e.changedTouches[0];
      if (!last) return;
      const dx = Math.abs(last.clientX - touchX);
      const dy = Math.abs(last.clientY - touchY);
      if (dx > TAP_MAX_MOVEMENT_PX || dy > TAP_MAX_MOVEMENT_PX) return;

      let clickFired = false;
      const onClickCheck = () => {
        clickFired = true;
      };
      document.addEventListener('click', onClickCheck, { capture: true, once: true });

      window.setTimeout(() => {
        document.removeEventListener('click', onClickCheck, { capture: true });
        if (clickFired) return;

        // iOS suppressed synthetic click. Dispatch one ourselves so React's
        // delegated event listener at the document root runs the React onClick
        // handler. `HTMLElement.click()` only exists on HTMLElement; SVG and
        // other Element subclasses need an explicit dispatchEvent fallback.
        const el = target as Element;
        if (typeof (el as HTMLElement).click === 'function') {
          (el as HTMLElement).click();
        } else {
          el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        }
      }, CLICK_WAIT_MS);
    };

    const onTouchCancel = () => {
      touchTarget = null;
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    document.addEventListener('touchcancel', onTouchCancel, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchCancel);
    };
  }, []);
};
